interface RichTextSegment {
    text: string
    bold: boolean
    italic: boolean
}

interface RichTextBlock {
    segments: RichTextSegment[]
    prefix: string
}

const PDF_FONT_VALUE = 10
const PDF_COL_PADDING = 2
const PDF_LINE_HEIGHT = 5

export const parseHtmlToBlocks = (html: string): RichTextBlock[] => {
    if (!html?.trim()) return []
    try {
        const parser = new DOMParser()
        const parsed = parser.parseFromString(html, 'text/html')
        const blocks: RichTextBlock[] = []
        const getSegs = (node: Node, bold = false, italic = false): RichTextSegment[] => {
            if (node.nodeType === 3 /* TEXT_NODE */) return [{ text: node.textContent ?? '', bold, italic }]
            if (node.nodeType !== 1 /* ELEMENT_NODE */) return []
            const el = node as Element
            const tag = el.tagName.toLowerCase()
            return Array.from(el.childNodes).flatMap(c =>
                getSegs(c, bold || tag === 'strong' || tag === 'b', italic || tag === 'em' || tag === 'i')
            )
        }
        const processEl = (el: Element, ulType?: 'ul' | 'ol', listIdx?: number) => {
            const tag = el.tagName.toLowerCase()
            if (tag === 'p' || tag === 'div') {
                const segs = getSegs(el)
                blocks.push({ segments: segs.length ? segs : [{ text: '', bold: false, italic: false }], prefix: '' })
            } else if (tag === 'ul' || tag === 'ol') {
                let idx = 0
                el.querySelectorAll(':scope > li').forEach(li => processEl(li, tag as 'ul' | 'ol', idx++))
            } else if (tag === 'li') {
                const prefix = ulType === 'ul' ? '  \u2022 ' : `  ${(listIdx ?? 0) + 1}. `
                const segs = getSegs(el)
                blocks.push({ segments: segs.length ? segs : [{ text: '', bold: false, italic: false }], prefix })
            } else if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
                blocks.push({ segments: [{ text: el.textContent ?? '', bold: true, italic: false }], prefix: '' })
            } else if (tag === 'br') {
                blocks.push({ segments: [{ text: '', bold: false, italic: false }], prefix: '' })
            } else {
                el.childNodes.forEach(c => { if (c.nodeType === 1) processEl(c as Element) })
            }
        }
        parsed.body.childNodes.forEach(c => { if (c.nodeType === 1) processEl(c as Element) })
        if (blocks.length === 0) {
            blocks.push({ segments: [{ text: html.replace(/<[^>]*>/g, ''), bold: false, italic: false }], prefix: '' })
        }
        return blocks
    } catch {
        return [{ segments: [{ text: html.replace(/<[^>]*>/g, ''), bold: false, italic: false }], prefix: '' }]
    }
}

export const richTextLineCount = (doc: any, blocks: RichTextBlock[], textWidth: number): number =>
    blocks.reduce((sum, b) => {
        const fullText = b.prefix + b.segments.map(s => s.text).join('')
        return sum + (doc.splitTextToSize(fullText || ' ', textWidth) as string[]).length
    }, 0)

export const renderRichTextBlocks = (doc: any, blocks: RichTextBlock[], x: number, textWidth: number, startY: number) => {
    let y = startY
    const lineStartX = x + PDF_COL_PADDING
    const maxX = lineStartX + textWidth

    blocks.forEach(block => {
        const hasContent = block.prefix || block.segments.some(s => s.text.trim())
        if (!hasContent) {
            y += PDF_LINE_HEIGHT
            return
        }

        let curX = lineStartX
        let pendingSpaceWidth = 0
        doc.setFontSize(PDF_FONT_VALUE)
        doc.setTextColor(0, 0, 0)

        const allSegs: RichTextSegment[] = block.prefix
            ? [{ text: block.prefix, bold: false, italic: false }, ...block.segments]
            : block.segments

        allSegs.forEach(seg => {
            if (!seg.text) return
            const style = seg.bold && seg.italic ? 'bolditalic' : seg.bold ? 'bold' : seg.italic ? 'italic' : 'normal'
            doc.setFont('helvetica', style)

            // Split into word / whitespace tokens for line-wrapping
            const parts = seg.text.split(/(\s+)/)
            parts.forEach(part => {
                if (!part) return
                if (/^\s+$/.test(part)) {
                    pendingSpaceWidth = doc.getTextWidth(' ')
                    return
                }
                const wordWidth = doc.getTextWidth(part)
                if (curX > lineStartX && curX + pendingSpaceWidth + wordWidth > maxX) {
                    // wrap to next line
                    y += PDF_LINE_HEIGHT
                    curX = lineStartX
                    pendingSpaceWidth = 0
                } else {
                    curX += pendingSpaceWidth
                    pendingSpaceWidth = 0
                }
                doc.text(part, curX, y)
                curX += wordWidth
            })
        })

        y += PDF_LINE_HEIGHT
    })

    doc.setFont('helvetica', 'normal')
}

export const renderLinkLine = (doc: any, text: string, url: string, x: number, y: number) => {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(PDF_FONT_VALUE)
    doc.setTextColor(0, 102, 204)
    doc.textWithLink(text, x + PDF_COL_PADDING, y, { url })
    const tw: number = doc.getTextWidth(text)
    doc.setDrawColor(0, 102, 204)
    doc.line(x + PDF_COL_PADDING, y + 0.8, x + PDF_COL_PADDING + tw, y + 0.8)
    doc.setTextColor(0, 0, 0)
    doc.setDrawColor(0, 0, 0)
}